import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { debounce, mediaQueryLarge, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { CollectionUpdateEvent, SearchUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * @typedef {Object} LenderParts
 * @property {HTMLElement} drawer - The filters theme-drawer
 * @property {HTMLDialogElement} dialog - The drawer's dialog
 * @property {HTMLElement} slot - The drawer's [data-bl-drawer-slot]
 * @property {HTMLElement} list - The bar's overflow list (the filter list's home)
 * @property {HTMLElement | null} scroller - The drawer's scrolling filter area
 */

/**
 * Attributes the bar's accordions carry and the drawer's do not, and the reverse (snippets/list-filter.liquid).
 * A moved accordion-custom reads them when the move reconnects it, and re-arms its listeners there (Escape, breakpoint;
 * assets/accordion-custom.js makes a fresh AbortController on every connect since R2_screens, 10/21).
 */
const ACCORDION_BAR_ONLY = ['data-disable-animation-on-desktop', 'data-close-with-escape'];
const ACCORDION_DRAWER_ONLY = ['open', 'open-by-default-on-desktop', 'open-by-default-on-mobile'];

/**
 * Borderline (R2, 2026-10-21): blocks/filters.liquid renders the horizontal filter list once, in the bar's overflow
 * list (it used to render a second copy in #filters-drawer: half of /search's 2 MB, every id twice). The drawer (the
 * phone sheet, and the bar's "More" panel) borrows the bar's nodes while it is open: they move into its
 * [data-bl-drawer-slot] when it opens and back when its dialog has closed, switching between the bar's popover
 * presentation and the drawer's accordion one (the attributes and classes the Liquid rendered differently for the two
 * copies). Section re-renders morph against the server markup, which has the list in the bar: #updateSection hands the
 * list back right before the morph and lends it again right after, in the same task, so nothing paints in between and
 * the morph keeps node identity; open accordions, the drawer's scroll and focus are carried over.
 */
class FilterLender {
  /** @type {{ scrollTop: number, open: Map<string, boolean>, focus: ReturnType<typeof FilterLender.focusState> } | null} */
  static #saved = null;

  /** @returns {LenderParts | null} */
  static parts() {
    const drawer = document.getElementById('filters-drawer');
    const slot = drawer?.querySelector('[data-bl-drawer-slot]');
    const dialog = drawer?.querySelector('dialog');
    const list = drawer?.closest('.shopify-section')?.querySelector('.facets--horizontal .facets__overflow-list');
    if (
      !(drawer instanceof HTMLElement) ||
      !(slot instanceof HTMLElement) ||
      !(dialog instanceof HTMLDialogElement) ||
      !(list instanceof HTMLElement)
    ) {
      return null;
    }
    const scroller = slot.closest('.facets-drawer__filters');
    return { drawer, dialog, slot, list, scroller: scroller instanceof HTMLElement ? scroller : null };
  }

  /**
   * @param {Element} container
   * @returns {HTMLElement[]} The filter items (accordion-custom.facets__item) directly in the container
   */
  static items(container) {
    return /** @type {HTMLElement[]} */ (
      Array.from(container.children).filter((element) => element.classList.contains('facets__item'))
    );
  }

  /** @param {LenderParts} parts */
  static isOpen(parts) {
    return parts.drawer.hasAttribute('open') && parts.dialog.open;
  }

  /**
   * The form that holds the filter inputs right now, when it is not the given one (the bar's sort changed while the
   * drawer holds the list).
   * @param {HTMLFormElement} form
   * @returns {HTMLFormElement | null}
   */
  static otherHolder(form) {
    const parts = FilterLender.parts();
    if (!parts || !FilterLender.items(parts.slot).length) return null;
    const holder = parts.slot.closest('form');
    return holder && holder !== form ? holder : null;
  }

  /**
   * Moves a node to the end of a parent, keeping focus where the browser can (moveBefore).
   * @param {Element} parent
   * @param {Element} node
   */
  static move(parent, node) {
    const atomic = /** @type {any} */ (parent).moveBefore;
    if (typeof atomic === 'function') {
      try {
        atomic.call(parent, node, null);
        return;
      } catch {
        // not movable atomically (e.g. across roots): plain insert below
      }
    }
    parent.append(node);
  }

  /**
   * Without moveBefore, moving the focused field blurs it, and a field with typed text commits (fires change) right
   * then, from a half-moved list in two forms: commit it before the first move, while the whole list is in one form.
   * @param {HTMLElement[]} items
   */
  static settleFocus(items) {
    if (typeof (/** @type {any} */ (Element.prototype).moveBefore) === 'function') return;
    const element = document.activeElement;
    if (element instanceof HTMLElement && items.some((item) => item.contains(element))) element.blur();
  }

  /** @param {Element} container */
  static dropWhitespace(container) {
    for (const node of Array.from(container.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE && !node.nodeValue?.trim()) node.remove();
    }
  }

  /** @param {HTMLElement[]} items */
  static focusState(items) {
    const element = document.activeElement;
    if (!(element instanceof HTMLElement) || !items.some((item) => item.contains(element))) return null;
    const typing =
      element instanceof HTMLInputElement && element.type === 'text'
        ? { value: element.value, start: element.selectionStart, end: element.selectionEnd }
        : null;
    return { element, id: element.id, typing };
  }

  /** @param {ReturnType<typeof FilterLender.focusState>} state */
  static restoreFocus(state) {
    if (!state) return;
    let { element } = state;
    if (!element.isConnected && state.id) element = document.getElementById(state.id) ?? element;
    if (!element.isConnected) return;
    if (document.activeElement !== element) element.focus({ preventScroll: true });
    // A text field (price) the shopper is typing in: a plain move blurs it, and the morph then rewrites its value.
    if (state.typing && element instanceof HTMLInputElement && element.value !== state.typing.value) {
      element.value = state.typing.value;
      element.setSelectionRange(state.typing.start, state.typing.end);
    }
  }

  /**
   * Switches a filter's <details> subtree between the bar's popover and the drawer's accordion presentation.
   * @param {HTMLDetailsElement} details
   * @param {'bar' | 'drawer'} where
   */
  static present(details, where) {
    const inDrawer = where === 'drawer';
    // the bar closes an open popover on any outside click (assets/auto-close-details.js); accordions stay open
    if (inDrawer) details.removeAttribute('data-auto-close-details');
    else details.setAttribute('data-auto-close-details', 'desktop');

    // show more: the bar lists every value in its popover (scroll box), the drawer shows 10 and a "Show more" button
    const showMore = details.querySelector('show-more-component');
    if (showMore) {
      if (inDrawer) showMore.removeAttribute('data-disable-on-desktop');
      else showMore.setAttribute('data-disable-on-desktop', 'true');
      const [from, to] = inDrawer ? ['mobile:hidden', 'hidden'] : ['hidden', 'mobile:hidden'];
      for (const item of showMore.querySelectorAll('[ref="showMoreItems[]"]')) {
        if (item.classList.contains(from)) item.classList.replace(from, to);
      }
      showMore.querySelector('.show-more__button')?.classList.toggle('desktop:hidden', !inDrawer);
    }

    // price: the bar's popover is at least 360px wide
    details.querySelector('price-facet-component')?.classList.toggle('price-facet--horizontal', !inDrawer);
    details
      .querySelector('.price-facet__highest-price')
      ?.classList.toggle('price-facet__highest-price--horizontal', !inDrawer);
  }

  /**
   * The bar shows one active value's label in a filter's summary, the drawer a count bubble.
   * @param {HTMLElement} item
   * @param {'horizontal' | 'vertical'} style
   */
  static restyleStatus(item, style) {
    const status = item.querySelector('facet-status-component[facet-type="list"]');
    if (!(status instanceof FacetStatusComponent) || status.dataset.filterStyle === style) return;
    status.dataset.filterStyle = style;
    if (!status.refs.facetStatus) return;
    const checked = /** @type {HTMLInputElement[]} */ (
      Array.from(item.querySelectorAll('input[type="checkbox"]:checked'))
    );
    status.updateListSummary(checked);
  }

  /**
   * Moves the bar's filters into the open drawer.
   * @param {LenderParts | null} [parts]
   */
  static lend(parts = FilterLender.parts()) {
    if (!parts) return;
    const items = FilterLender.items(parts.list);
    if (!items.length) return;

    const focus = FilterLender.focusState(items);
    FilterLender.settleFocus(items);
    FilterLender.dropWhitespace(parts.list);
    FilterLender.dropWhitespace(parts.slot);

    for (const item of items) {
      const details = item.querySelector(':scope > details');
      if (!(details instanceof HTMLDetailsElement)) continue;
      // accordion attributes first: accordion-custom reads them when the move reconnects it
      for (const name of ACCORDION_BAR_ONLY) item.removeAttribute(name);
      for (const name of ACCORDION_DRAWER_ONLY) item.setAttribute(name, '');
      FilterLender.present(details, 'drawer');
      FilterLender.move(parts.slot, item);
      details.open = true;
    }
    for (const item of items) FilterLender.restyleStatus(item, 'vertical');

    FilterLender.restoreFocus(focus);
    // scroll-hint recomputes its fade on scroll or on its own resize; its content just changed
    parts.scroller?.dispatchEvent(new Event('scroll'));
  }

  /**
   * Moves the lent filters home to the bar (popover presentation).
   * @param {LenderParts | null} [parts]
   */
  static giveBack(parts = FilterLender.parts()) {
    if (!parts) return;
    FilterLender.settleFocus(FilterLender.items(parts.slot));
    FilterLender.dropWhitespace(parts.slot);
    FilterLender.dropWhitespace(parts.list);

    for (const item of FilterLender.items(parts.slot)) {
      const details = item.querySelector(':scope > details');
      if (details instanceof HTMLDetailsElement) {
        details.open = false;
        FilterLender.present(details, 'bar');
      }
      for (const name of ACCORDION_DRAWER_ONLY) item.removeAttribute(name);
      item.setAttribute('data-disable-animation-on-desktop', 'true');
      item.setAttribute('data-close-with-escape', 'true');
      FilterLender.move(parts.list, item);
    }

    for (const item of FilterLender.items(parts.list)) FilterLender.restyleStatus(item, 'horizontal');
  }

  /** Puts the list where the drawer's state says it belongs. */
  static sync() {
    const parts = FilterLender.parts();
    if (!parts) return;
    if (FilterLender.isOpen(parts)) FilterLender.lend(parts);
    else FilterLender.giveBack(parts);
  }

  /** Before a section morph: the list goes home (positions only; the morph rewrites the presentation attributes). */
  static beforeMorph() {
    const parts = FilterLender.parts();
    if (!parts) return;
    const items = FilterLender.items(parts.slot);
    if (!items.length) return;

    FilterLender.#saved = {
      scrollTop: parts.scroller?.scrollTop ?? 0,
      open: new Map(
        items.map((item) => {
          const details = item.querySelector(':scope > details');
          return [FilterLender.key(item), details instanceof HTMLDetailsElement && details.open];
        })
      ),
      focus: FilterLender.focusState(items),
    };

    FilterLender.settleFocus(items);
    FilterLender.dropWhitespace(parts.slot);
    FilterLender.dropWhitespace(parts.list);
    for (const item of items) FilterLender.move(parts.list, item);
  }

  /** After a section morph (or a failed render): lend the list again if the drawer is open, restore its state. */
  static afterMorph() {
    const saved = FilterLender.#saved;
    FilterLender.#saved = null;
    FilterLender.sync();

    const parts = FilterLender.parts();
    if (!saved || !parts || !FilterLender.isOpen(parts)) return;
    for (const item of FilterLender.items(parts.slot)) {
      const details = item.querySelector(':scope > details');
      const open = saved.open.get(FilterLender.key(item));
      if (details instanceof HTMLDetailsElement && open !== undefined) details.open = open;
    }
    if (parts.scroller) parts.scroller.scrollTop = saved.scrollTop;
    FilterLender.restoreFocus(saved.focus);
  }

  /**
   * @param {HTMLElement} item
   * @returns {string}
   */
  static key(item) {
    return item.querySelector(':scope > details')?.id || item.dataset.filterParamName || 'price';
  }
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /** @type {AbortController | null} */
  #lenderEvents = null;

  connectedCallback() {
    super.connectedCallback();
    // In vertical filter mode the in-page filters take over at ≥750px, so a
    // drawer left open across a mobile→desktop resize would render duplicate
    // filter UI. `data-filter-style` is only set on the drawer variant (see
    // blocks/filters.liquid), so matching "vertical" here uniquely targets it.
    if (this.dataset.filterStyle === 'vertical') {
      mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    }

    // Borderline (R2): the drawer variant with an empty slot borrows the bar's filter list (FilterLender)
    const drawer = this.closest('theme-drawer');
    const dialog = drawer?.querySelector('dialog');
    if (drawer && dialog && this.querySelector('[data-bl-drawer-slot]')) {
      this.#lenderEvents?.abort();
      this.#lenderEvents = new AbortController();
      const { signal } = this.#lenderEvents;
      drawer.addEventListener('theme-drawer:open', () => FilterLender.lend(), { signal });
      // fires once the slide-out has finished (and on a modal ↔ sidebar switch, when the drawer is open again)
      dialog.addEventListener('close', () => FilterLender.sync(), { signal });
      if (drawer.hasAttribute('open')) FilterLender.lend();
    }
  }

  updatedCallback() {
    super.updatedCallback();
    // a re-render this module did not start (theme editor) leaves the list in the bar
    if (this.#lenderEvents) FilterLender.sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    this.#lenderEvents?.abort();
    this.#lenderEvents = null;
  }

  #onBreakpointChange = (/** @type {MediaQueryListEvent} */ event) => {
    if (!event.matches) return;
    const drawer = document.getElementById('filters-drawer');
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  };

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = this.#formData()) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    // Borderline (R2): the drawer's sorting renders a select (phones) and radios (desktop) in one form, so a filter
    // ticked in the drawer sent sort_by twice. Both hold the current sort (a sort change disables the other control
    // while it submits); keep the first.
    const sortValues = newParameters.getAll('sort_by');
    if (sortValues.length > 1) newParameters.set('sort_by', sortValues[0] ?? '');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * The form's data. While the drawer holds the filter list (FilterLender), the bar's own form has no filter inputs:
   * a sort changed in the bar then takes the filters from the drawer's form.
   * @returns {FormData}
   */
  #formData() {
    const formData = new FormData(this.refs.facetsForm);
    const holder = FilterLender.otherHolder(this.refs.facetsForm);
    if (holder) {
      for (const [key, value] of new FormData(holder)) {
        if (key.startsWith('filter.')) formData.append(key, value);
      }
    }
    return formData;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  };

  /**
   * Updates the section
   * @returns {Promise<void>}
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');
    // Borderline (R2): the morph compares with the server markup, where the filter list sits in the bar (FilterLender)
    const render = () =>
      sectionRenderer
        .renderSection(this.sectionId, {
          shouldRender: () => {
            FilterLender.beforeMorph();
            return true;
          },
        })
        .finally(() => FilterLender.afterMorph());

    if (viewTransition) {
      return startViewTransition(render, ['product-grid']);
    } else {
      return render().then(() => {});
    }
  }

  /**
   * Dispatches search:update event if on the search page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchSearchUpdateEvent(renderPromise) {
    const query = this.#getSearchQuery();

    const deferredPromise = SearchUpdateEvent.createPromise();
    const productFilters = SearchUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query,
          ...(productFilters && { productFilters }),
          sortKey: SearchUpdateEvent.getSortKey(),
        },
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with result count
    renderPromise
      .then(() => deferredPromise.resolve({ totalCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }

  /**
   * Gets the count of product results from the server-rendered data attribute.
   * @returns {number}
   */
  #getResultsCount() {
    return Number(this.dataset.resultsCount) || 0;
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    const renderPromise = this.#updateSection();
    if (this.dataset.pageType === 'search') {
      this.#dispatchSearchUpdateEvent(renderPromise);
    } else {
      this.#dispatchCollectionUpdateEvent(renderPromise);
    }
  }

  /**
   * Dispatches collection:update event if on a collection page.
   * @param {Promise<void>} renderPromise - The promise from section rendering
   */
  #dispatchCollectionUpdateEvent(renderPromise) {
    // Build collection object with available identifiers
    // Only use numeric IDs — non-numeric values like "all" fail GID validation
    /** @type {any} */
    const rawId = this.dataset.collectionId;
    const collectionData = {
      id: rawId && /^\d+$/.test(rawId) ? rawId : null,
      handle: /** @type {string} */ (this.dataset.collectionHandle),
      productsCount: this.#getResultsCount(),
    };

    const deferredPromise = CollectionUpdateEvent.createPromise();
    const productFilters = CollectionUpdateEvent.parseProductFilters();

    this.dispatchEvent(
      new CollectionUpdateEvent({
        collection: collectionData,
        ...(productFilters && { productFilters }),
        sortKey: CollectionUpdateEvent.getSortKey(),
        promise: deferredPromise.promise,
      })
    );

    // Resolve promise after section renders with product count
    renderPromise
      .then(() => deferredPromise.resolve({ productsCount: this.#getResultsCount() }))
      .catch((error) => deferredPromise.reject(error));
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Fires immediately on pointerdown so a quick tap triggers the prefetch.
   * @param {PointerEvent} event
   */
  prefetchPageImmediate(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }

  /**
   * Reads `checked` at call time, so callers must invoke it before the toggle
   * for the predicted post-toggle URL to be correct.
   * @param {HTMLElement} optionElement
   */
  #prefetchOption(optionElement) {
    const form = this.closest('form');
    if (!form) return;

    const inputElement = optionElement.querySelector('input');
    if (!(inputElement instanceof HTMLInputElement) || inputElement.disabled) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const formData = new FormData(form);
    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const urlParameters = facetsForm.createURLParameters(formData);
    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    // fire-and-forget warm-up: a refused request (the store's 429) must not surface as an unhandled rejection; the
    // real render asks again (section-renderer.js does not cache failures)
    sectionRenderer.getSectionHTML(this.sectionId, true, url).catch(() => {});
  }

  /**
   * @param {PointerEvent} event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;
    this.#prefetchOption(event.target);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
  }

  /**
   * Marks the focused price input so a section morph triggered by the other price input
   * refreshes server-owned attributes without clobbering digits the shopper is still typing.
   * skipsValueUpdate wants the marker on both nodes plus live focus; PRESERVED_ATTRIBUTES
   * supplies the incoming node's copy, and it ignores empty values, hence 'true' and not ''.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.target.setAttribute('data-skip-value-update', 'true');
  };

  /**
   * Clears the marker when the edit ends. Hygiene rather than load-bearing: skipsValueUpdate
   * requires live focus too, so a marker left behind is inert once focus moves on.
   * `change` fires before `focusout`, so the typed value is already applied by this point.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.target.removeAttribute('data-skip-value-update');
  };

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle('facets__clear--active', filters.length > 0);
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {SearchUpdateEvent | CollectionUpdateEvent} event
   */
  #handleFilterUpdate = (/** @type {SearchUpdateEvent | CollectionUpdateEvent} */ event) => {
    // Ignore events from other sections (e.g. predictive search in header)
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('.shopify-section');
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      const filters =
        event instanceof SearchUpdateEvent ? event.search?.productFilters ?? [] : event.productFilters ?? [];
      clearButton.classList.toggle(activeClass, filters.length > 0);
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = inputElement.getAttribute('aria-label') ?? '';
        return (swatch?.outerHTML ?? '') + span.outerHTML;
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `<bdi>${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}</bdi>`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
